/**
 * Lightweight translation hook.
 *
 * THIN WRAPPER: this hook now delegates to the unified resolver
 * (i18n/resolve.ts) so it resolves the FULL dictionary chain
 * (core → portal → demo → general) exactly like useGlobalTranslation. Previously
 * it resolved only the general dictionary, which silently diverged from the
 * global hook and leaked English into the Spanish experience.
 *
 * Prefer useGlobalTranslation for new code (the ESLint guardrail steers you
 * there). This hook is kept as a back-compat shim for existing call sites.
 *
 * The general dictionary and its accessors now live in i18n/generalDictionary.ts.
 * translateText / getTranslationsForLanguage are re-exported here so existing
 * imports from '@/hooks/useTranslation' keep working.
 */

import { useMemo } from 'react';
import { useSettings } from '@/contexts/SettingsContext';
import { getTranslationsForLanguage } from '@/i18n/generalDictionary';
import { resolveTranslation } from '@/i18n/resolve';

export { getTranslationsForLanguage };

export function useTranslation() {
  const { settings } = useSettings();

  const t = useMemo(() => {
    return (key: string, fallback?: string): string =>
      resolveTranslation(key, settings.language, fallback);
  }, [settings.language]);

  return { t, currentLanguage: settings.language };
}

/**
 * Direct translation function for non-React contexts (chart formatters,
 * utilities, performance-critical code). Walks the full resolution chain via
 * the unified resolver — NOT just the general dictionary — so it matches what
 * components render.
 *
 * Back-compat: the previous translateText resolved only the general dictionary.
 * Routing it through the resolver is strictly a superset (it can now find
 * core/portal/demo keys too), which is the whole point of the unification.
 */
export function translateText(key: string, language: string = 'en', fallback?: string): string {
  return resolveTranslation(key, language, fallback);
}

/**
 * Format numbers according to locale for charts and financial data.
 */
export function formatNumber(
  value: number,
  language: string = 'en',
  options?: Intl.NumberFormatOptions,
): string {
  const localeMap: Record<string, string> = {
    en: 'en-US',
    es: 'es-ES',
    'fr-ca': 'fr-CA',
    'pt-br': 'pt-BR',
  };

  const locale = localeMap[language] || 'en-US';
  return new Intl.NumberFormat(locale, options).format(value);
}

/**
 * Format currency according to locale.
 */
export function formatCurrency(
  value: number,
  currency: string = 'USD',
  language: string = 'en',
): string {
  return formatNumber(value, language, {
    style: 'currency',
    currency: currency,
  });
}
